import type { I18nKeys } from '@shared/types/i18n.generated';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Props for PlaceholderPage component.
 * ONLY accepts type-safe i18n keys - no raw strings allowed.
 */
interface PlaceholderPageProps {
  /**
   * Type-safe i18n key for the page title.
   * Must be a valid key from translation.json.
   */
  titleKey: I18nKeys;

  /**
   * Type-safe i18n key for the page description.
   * Must be a valid key from translation.json.
   */
  descriptionKey?: I18nKeys;
}

/**
 * Placeholder page component for unimplemented features.
 * Displays the page title and current route path.
 *
 * Only accepts type-safe i18n keys. Passing string literals
 * will cause a TypeScript compilation error.
 *
 * @example
 * ```tsx
 * // Correct usage - i18n keys only
 * <PlaceholderPage
 *   titleKey="menu.accountSearch"
 *   descriptionKey="modules.procedures.description"
 * />
 *
 * // This will cause TypeScript error:
 * <PlaceholderPage
 *   titleKey="登録アカウント検索"  // ❌ Error: not a valid I18nKeys
 * />
 * ```
 */
export default function PlaceholderPage({ titleKey, descriptionKey }: Readonly<PlaceholderPageProps>) {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <div className="p-8">
      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t(titleKey)}</h1>
        <p className="text-sm text-gray-500 mb-6">
          Path: <code className="bg-gray-100 px-2 py-0.5 rounded">{location.pathname}</code>
        </p>

        {descriptionKey && <p className="text-gray-600 mb-6">{t(descriptionKey)}</p>}

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Warning Icon</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h3 className="font-semibold text-yellow-800">{t('common.errors.notFound')}</h3>
              <p className="text-sm text-yellow-700 mt-1">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
